import React, { useState, useEffect } from 'react';
import {
    Sparkles, Save, Trash2, Edit3, LogOut, LogIn,
    Loader2, AlertCircle, CheckCircle, ArrowLeft, Plus, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import suggestedTopics from '../data/suggested-topics';

const BlogAdmin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [posts, setPosts] = useState([]);

    // Login form
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    // Editor state
    const [isEditing, setIsEditing] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [topic, setTopic] = useState('');
    const [title, setTitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [content, setContent] = useState('');

    // UI state
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [usedTopics, setUsedTopics] = useState([]);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getToken = () => localStorage.getItem('blog_admin_token');

    const checkAuth = async () => {
        const token = getToken();
        if (!token) {
            setIsLoading(false);
            return;
        }

        try {
            const res = await fetch('/.netlify/functions/auth/verify', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.valid) {
                setIsAuthenticated(true);
                fetchPosts();
            } else {
                localStorage.removeItem('blog_admin_token');
            }
        } catch {
            localStorage.removeItem('blog_admin_token');
        }
        setIsLoading(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError('');

        try {
            const res = await fetch('/.netlify/functions/auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await res.json();

            if (data.success && data.token) {
                localStorage.setItem('blog_admin_token', data.token);
                setIsAuthenticated(true);
                fetchPosts();
            } else {
                setLoginError(data.error || 'Login failed');
            }
        } catch {
            setLoginError('Connection error');
        }
        setLoginLoading(false);
    };

    const handleLogout = () => {
        localStorage.removeItem('blog_admin_token');
        setIsAuthenticated(false);
        setPosts([]);
    };

    const fetchPosts = async () => {
        try {
            const res = await fetch('/.netlify/functions/blog-posts');
            const data = await res.json();
            setPosts(data.posts || []);
            setUsedTopics(data.usedTopics || []);
        } catch {
            showMessage('Failed to load posts', 'error');
        }
    };

    const handleTopicClick = async (selectedTopic) => {
        // Immediately mark as used so it disappears from suggestions
        setUsedTopics(prev => [...prev, selectedTopic]);
        setTopic(selectedTopic);
        // Auto-generate after setting topic
        setTimeout(() => {
            handleGenerate(selectedTopic);
        }, 100);
    };

    const handleGenerate = async (topicToUse = null) => {
        const currentTopic = topicToUse || topic;
        if (!currentTopic.trim()) {
            showMessage('Please enter a topic or select a suggestion', 'error');
            return;
        }

        setGenerating(true);
        try {
            const res = await fetch('/.netlify/functions/generate-blog', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify({ topic: currentTopic })
            });
            const data = await res.json();

            if (data.success) {
                setTitle(data.title);
                setExcerpt(data.excerpt);
                setContent(data.content);
                showMessage('Content generated! Review and publish when ready.', 'success');
            } else {
                showMessage(data.error || 'Generation failed', 'error');
            }
        } catch {
            showMessage('Failed to generate content', 'error');
        }
        setGenerating(false);
    };

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) {
            showMessage('Title and content are required', 'error');
            return;
        }

        setSaving(true);
        try {
            const endpoint = '/.netlify/functions/blog-posts';
            const method = editingPost ? 'PUT' : 'POST';
            const payload = editingPost
                ? { id: editingPost.id, title, excerpt, content }
                : { title, excerpt, content, usedTopic: topic };

            const res = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.success) {
                showMessage(editingPost ? 'Post updated!' : 'Post published!', 'success');
                clearEditor();
                fetchPosts();
            } else {
                showMessage(data.error || 'Save failed', 'error');
            }
        } catch {
            showMessage('Failed to save post', 'error');
        }
        setSaving(false);
    };

    const handleEdit = (post) => {
        setEditingPost(post);
        setTitle(post.title);
        setExcerpt(post.excerpt);
        setContent(post.content);
        setIsEditing(true);
        setTopic('');
    };

    const handleDelete = async (post) => {
        if (!window.confirm(`Delete "${post.title}"?`)) return;

        try {
            const res = await fetch('/.netlify/functions/blog-posts', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify({ id: post.id })
            });
            const data = await res.json();

            if (data.success) {
                showMessage('Post deleted', 'success');
                fetchPosts();
            } else {
                showMessage(data.error || 'Delete failed', 'error');
            }
        } catch {
            showMessage('Failed to delete post', 'error');
        }
    };

    const clearEditor = () => {
        setEditingPost(null);
        setTitle('');
        setExcerpt('');
        setContent('');
        setTopic('');
        setIsEditing(false);
    };

    const showMessage = (text, type) => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: '#0d0d0d' }}>
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
        );
    }

    // Login form
    if (!isAuthenticated) {
        return (
            <>
                <Navigation scrolled={scrolled} />
                <div className="min-h-screen flex items-center justify-center px-4 pt-20" style={{ background: '#0d0d0d' }}>
                    <div className="w-full max-w-md">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold gradient-text mb-2">Blog Admin</h1>
                            <p className="text-gray-500">Sign in to manage blog posts</p>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div
                                className="p-8 rounded-2xl border"
                                style={{
                                    background: 'rgba(20, 20, 25, 0.9)',
                                    borderColor: 'rgba(0, 212, 255, 0.2)'
                                }}
                            >
                                {loginError && (
                                    <div className="flex items-center space-x-2 text-red-400 text-sm mb-4 p-3 rounded-lg bg-red-500/10">
                                        <AlertCircle className="w-4 h-4" />
                                        <span>{loginError}</span>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-2">Username</label>
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className="w-full px-4 py-3 rounded-lg bg-black/50 border border-gray-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-gray-400 mb-2">Password</label>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-4 py-3 rounded-lg bg-black/50 border border-gray-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                                            required
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loginLoading}
                                    className="w-full mt-6 py-3 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-all disabled:opacity-50"
                                    style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
                                >
                                    {loginLoading ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <LogIn className="w-5 h-5" />
                                            <span>Sign In</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <div className="text-center mt-6">
                            <Link to="/" className="text-gray-500 hover:text-primary-400 transition-colors text-sm">
                                ← Back to site
                            </Link>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Admin dashboard
    return (
        <>
            <Navigation scrolled={scrolled} />

            <div className="min-h-screen pt-24 pb-12 px-4" style={{ background: '#0d0d0d' }}>
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center space-x-4">
                            <Link to="/blog" className="text-gray-500 hover:text-primary-400 transition-colors">
                                <ArrowLeft className="w-5 h-5" />
                            </Link>
                            <h1 className="text-2xl font-bold gradient-text">Blog Admin</h1>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center space-x-2 text-gray-400 hover:text-red-400 transition-colors"
                        >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Logout</span>
                        </button>
                    </div>

                    {/* Message Toast */}
                    {message && (
                        <div
                            className={`fixed top-24 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${message.type === 'error' ? 'bg-red-500/90' : 'bg-green-500/90'
                                }`}
                        >
                            {message.type === 'error' ? (
                                <AlertCircle className="w-4 h-4 text-white" />
                            ) : (
                                <CheckCircle className="w-4 h-4 text-white" />
                            )}
                            <span className="text-white text-sm">{message.text}</span>
                        </div>
                    )}

                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Editor Panel */}
                        <div
                            className="p-6 rounded-2xl border"
                            style={{
                                background: 'rgba(20, 20, 25, 0.9)',
                                borderColor: 'rgba(0, 212, 255, 0.2)'
                            }}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold text-white">
                                    {editingPost ? 'Edit Post' : 'New Post'}
                                </h2>
                                {(isEditing || title || content) && (
                                    <button
                                        onClick={clearEditor}
                                        className="text-gray-500 hover:text-gray-300 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>

                            {/* Suggested Topics */}
                            {!editingPost && (() => {
                                const availableTopics = suggestedTopics.filter(t => !usedTopics.includes(t));
                                return availableTopics.length > 0 ? (
                                    <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-medium text-purple-300">Quick Topic Ideas</h3>
                                            <span className="text-xs text-gray-500">
                                                {availableTopics.length}/{suggestedTopics.length} available
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                                            {availableTopics.map((suggestedTopic, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleTopicClick(suggestedTopic)}
                                                    disabled={generating}
                                                    className="px-3 py-1.5 text-xs rounded-full bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 border border-purple-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                                                >
                                                    {suggestedTopic}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : null;
                            })()}

                            {/* AI Generation */}
                            {!editingPost && (
                                <div className="mb-6 p-4 rounded-xl bg-black/30 border border-gray-800">
                                    <div className="flex items-center space-x-2 text-primary-400 mb-3">
                                        <Sparkles className="w-4 h-4" />
                                        <span className="text-sm font-medium">AI Generate</span>
                                    </div>
                                    <div className="flex space-x-2">
                                        <input
                                            type="text"
                                            value={topic}
                                            onChange={(e) => setTopic(e.target.value)}
                                            placeholder="Enter topic (e.g., 'QA automation ROI')"
                                            className="flex-1 px-3 py-2 rounded-lg bg-black/50 border border-gray-700 text-white text-sm focus:border-primary-500 focus:outline-none"
                                        />
                                        <button
                                            onClick={handleGenerate}
                                            disabled={generating}
                                            className="px-4 py-2 rounded-lg font-medium text-white text-sm flex items-center space-x-2 transition-all disabled:opacity-50"
                                            style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
                                        >
                                            {generating ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="w-4 h-4" />
                                            )}
                                            <span>Generate</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Form Fields */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2">Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg bg-black/50 border border-gray-700 text-white focus:border-primary-500 focus:outline-none transition-colors"
                                        placeholder="Post title..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2">Excerpt</label>
                                    <textarea
                                        value={excerpt}
                                        onChange={(e) => setExcerpt(e.target.value)}
                                        rows={2}
                                        className="w-full px-4 py-3 rounded-lg bg-black/50 border border-gray-700 text-white focus:border-primary-500 focus:outline-none transition-colors resize-none"
                                        placeholder="Short preview text..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-2">Content</label>
                                    <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        rows={10}
                                        className="w-full px-4 py-3 rounded-lg bg-black/50 border border-gray-700 text-white focus:border-primary-500 focus:outline-none transition-colors resize-none"
                                        placeholder="Full blog content..."
                                    />
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSave}
                                disabled={saving || !title || !content}
                                className="w-full mt-6 py-3 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
                            >
                                {saving ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span>{editingPost ? 'Update Post' : 'Publish Post'}</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Posts List */}
                        <div
                            className="p-6 rounded-2xl border"
                            style={{
                                background: 'rgba(20, 20, 25, 0.9)',
                                borderColor: 'rgba(0, 212, 255, 0.2)'
                            }}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-semibold text-white">Published Posts</h2>
                                <span className="text-sm text-gray-500">{posts.length} posts</span>
                            </div>

                            {posts.length === 0 ? (
                                <div className="text-center py-12">
                                    <Plus className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                                    <p className="text-gray-500">No posts yet</p>
                                    <p className="text-gray-600 text-sm">Generate your first post using AI</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                                    {posts.map((post) => (
                                        <div
                                            key={post.id}
                                            className="p-4 rounded-xl bg-black/30 border border-gray-800 hover:border-gray-700 transition-colors group"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0 pr-4">
                                                    <h3 className="font-medium text-white truncate">{post.title}</h3>
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{post.excerpt}</p>
                                                    <div className="flex items-center space-x-3 mt-2 text-xs text-gray-600">
                                                        <span>{formatDate(post.publishedAt)}</span>
                                                        <span>•</span>
                                                        <span>{post.readTime} min read</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => handleEdit(post)}
                                                        className="p-2 text-gray-400 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-all"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(post)}
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BlogAdmin;
